import axios, { AxiosError } from 'axios';
import { clearAuthSessionAndRedirect, ResetTokenAndReattemptRequest } from './authTokenHandler';
import {
  ACCESS_TOKEN_KEY,
  getRefreshTokenFromStorage,
  REFRESH_TOKEN_KEY,
  removeAuthTokenFromStorage,
} from '../auth';
import { captureException, MODULE_TYPES } from '@sdf/base';

const axiosInstance = axios.create({
  baseURL: process.env['NEXT_PUBLIC_API_BASE_URL'] || '',
});

axiosInstance.interceptors.request.use(
  (config) => {
    if (config.headers?.skipAuth) {
      delete config.headers.skipAuth;
      return config;
    }

    // If an OAuth/PKCE flow or impersonation transition is in progress, avoid attaching tokens
    try {
      const inAuthFlow =
        typeof window !== 'undefined' &&
        ((window.sessionStorage &&
          (window.sessionStorage.getItem('oauth_state') ||
            window.sessionStorage.getItem('pkce_code_verifier'))) ||
          (window.localStorage &&
            (window.localStorage.getItem('oauth_state_bak') ||
              window.localStorage.getItem('pkce_code_verifier_bak'))));
      if (inAuthFlow) {
        return config;
      }
    } catch {}

    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      config.headers['access_token'] = `${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const url = error?.config?.url;
    captureException(error?.response?.data?.message, {
      tags: { error_type: 'axios_api_error', module: MODULE_TYPES.API, url: url },
      extra: error.response ?? error,
    });

    const refresh_token = getRefreshTokenFromStorage();
    if (error.response?.status === 401 && refresh_token) {
      return ResetTokenAndReattemptRequest(error);
    } else if (error.response?.status === 401 && !refresh_token) {
      // Suppress redirect during OAuth callback/PKCE handoff to avoid race conditions
      try {
        const path = typeof window !== 'undefined' ? window.location.pathname : '';
        const inCallback = path.startsWith('/auth/callback');
        const inAuthFlow =
          typeof window !== 'undefined' &&
          ((window.sessionStorage &&
            (window.sessionStorage.getItem('oauth_state') ||
              window.sessionStorage.getItem('pkce_code_verifier'))) ||
            (window.localStorage &&
              (window.localStorage.getItem('oauth_state_bak') ||
                window.localStorage.getItem('pkce_code_verifier_bak'))));
        if (inCallback || inAuthFlow) {
          return Promise.reject(error);
        }
      } catch {}

      removeAuthTokenFromStorage();
      window.location.href = '/';
    }

    return Promise.reject(error);
  }
);

function extractErrorMessage(error: AxiosError) {
  if (error?.response?.data) {
    return error.response.data;
  } else if (error?.response) {
    return error.response;
  } else {
    return error;
  }
}

export default axiosInstance;
